import { useLightboxContext } from "@sphtech/idpf-lightbox-react";
import {
  ResponseType,
  TRouteWithoutRedirect,
} from "@sphtech/web2-core/ssr";
import Header from "@src/app/components/Header";
import TileView from "@src/app/components/TileView";
import cx from "classnames";
import dayjs from "dayjs";
import Cookies from "js-cookie";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import AppContext from "src/app/AppContext";
import ButtonLoading from "src/app/assets/button-loading.json";
import { saveCookiePath } from "src/app/auth/cookiePath";
import {
  NO_AUTH_PUBLICATION,
  PublicationPathName,
  PublicationShortname,
  PublicationSpecialPathName,
} from "src/app/auth/serviceIdentifier";
import { isEntitledForPub } from "src/app/auth/subscriberFlags";
import { DrawerContainer } from "src/app/components/DrawerContainer/index";
import IssueContainer from "src/app/components/IssuesContainer";
import Lottie from "src/app/components/Lottie";
import MultiTitle from "src/app/components/MultiTitle";
import { OnboardingModal } from "src/app/components/OnboardingModal";
import { OnboardingOverlay } from "src/app/components/OnboardingOverlay";
import PreviewBottomBar from "src/app/components/PreviewBottomBar";
import PreviewGateModal from "src/app/components/PreviewGateModal";
import {
  isPreviewEnabledForPub,
  PREVIEW_SUBSCRIBE_URL,
  shouldShowNewOnboarding,
} from "src/app/config/featureFlags";
import { NLB_SESSION_EXPIRED_URL } from "src/app/data.client";
import useDateFormat from "src/app/hooks/useDateFormat";
import { Actions } from "src/app/reducer";
import { Language } from "src/app/types/Data.d";
import { getScreenOrientation } from "src/app/utils/getScreenOrientation";
import {
  isNlbSessionExpired,
  isNlbUser,
  persistNlbOriginIfPresent,
} from "src/app/utils/userType";
import {
  EP_GA_EVENT_NAME,
  GAClickAction,
  getGAAuthenticationStatus,
  getGAPublicationName,
  getTrackerDateString,
  PAGE_NAME,
  PREVIEW_CD_VERSION,
  useEpGTMDispatch,
} from "src/trackers/googleAnalytics";
import { DEFAULT_VISITOR_CONTEXT } from "src/trackers/viewerContext";

import assertEnum from "../../../../.web2/web2-helpers/assertEnum";
import ErrorPage from "../ErrorPage";
import {
  fetchArchives,
  fetchPreviewArchive,
  getIssueById,
  pickPreviewIssueForDate,
  TArchive,
} from "./api";
import styles from "./Home.module.scss";
import { HomePagePayload } from "./Home.server";

const YMD_FORMAT = "YYYYMMDD";

export enum ISSUE_GA_CLICK_LABEL {
  IMAGE_CLICK = "start reading front page",
  START_READING_BTN = "start reading",
  CLOSE_ONBOARDING_MODAL = "onboarding",
  INFO_BUTTON = "info",
  TILE_VIEW_BUTTON = "archive",
  CALENDAR = "calendar",
  LOGOUT_BUTTON = "logout",
  // Preview (IRISX-45): the two web preview clicks per the GA Tagging Plan.
  PREVIEW_ISSUE = "preview issue",
  EXPLORE_SUBSCRIPTION = "explore subscription",
  // Subscribe page CTAs (shared with SubscribePage).
  SUBSCRIBE_NOW = "subscribe now",
  SUBSCRIBE_LATER = "not now",
}

export const COOKIE_KEY_PUBS_ID = "sph_pubsId";
export const COOKIE_ISSUE_ID = "sph_issueId";
export const COOKIE_PATH_PUBS_ID = "sph_path_pubsId";

type HomePageContextType = {
  toggleDrawer: () => void;
  selectedDateMap: Map<PublicationShortname, Date>;
  setSelectedDateMap: (_: Map<PublicationShortname, Date>) => void;
  calendarData: Record<string, TArchive[]> | null;
  isMobileView: boolean;
  issues: Partial<TArchive>[];
};
export const HomePageContext = React.createContext<HomePageContextType>({
  toggleDrawer: () => {},
  setSelectedDateMap: () => {},
  calendarData: null,
  isMobileView: false,
  selectedDateMap: new Map<PublicationShortname, Date>(),
  issues: [],
});

export type TGtmData = {
  clickAction?: GAClickAction;
  clickLabel?: ISSUE_GA_CLICK_LABEL | "success" | "failed";
  issue_pub_date?: string;
  chapter1: string;
  pageName: string;
  pagination?: number;
  cd_version?: string;
};

function getOnboardingKey(key: string): string {
  return `${key}_visited`;
}

/**
 * this function is used to check the user is visiting first time or not.
 * when the value of the key is null it represents user visiting for the first time
 */
function getShouldShowOnboarding(
  publicationId: PublicationShortname,
  secondaryPublicationId: PublicationShortname | null,
): boolean {
  if (secondaryPublicationId) {
    return (
      localStorage.getItem(getOnboardingKey(publicationId)) == null &&
      localStorage.getItem(getOnboardingKey(secondaryPublicationId)) == null
    );
  } else {
    return localStorage.getItem(getOnboardingKey(publicationId)) == null;
  }
}

type Props = {
  lang: Language;
  h1Text?: string;
  secondaryPublicationId?: PublicationShortname | null;
  navTabBarIndex?: number | null;
  context: TRouteWithoutRedirect<HomePagePayload, string>;
};

function HomePage({
  lang,
  h1Text,
  secondaryPublicationId = null,
  navTabBarIndex = null,
  context,
}: Props): React.ReactElement {
  const { i18n, t } = useTranslation();

  const { visitorContext, viewerContextResolved, dispatch } =
    useContext(AppContext);
  const { isAuthenticated, lightbox } = useLightboxContext();

  const [showDrawer, setShowDrawer] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showTileView, setTileView] = useState(false);
  const [showPreviewGate, setShowPreviewGate] = useState(false);
  const [issues, setIssues] = useState<TArchive[]>([]);
  const [calendarData, setCalendarData] =
    useState<HomePageContextType["calendarData"]>(null);
  const [isMobileView, setIsMobileView] = useState<boolean>(false);

  const [isLoading, setIsLoading] = useState(false);
  const [isWaitingForAuth, setIsWaitingForAuth] = useState(false);
  const [authTimedOut, setAuthTimedOut] = useState(false);

  const LIGHTBOX_TIMEOUT_MS = 5000;
  const lightboxRef = useRef(lightbox);
  const lightboxResolveRef = useRef<(() => void) | null>(null);
  const lightboxTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    lightboxRef.current = lightbox;
    if (lightbox && lightboxResolveRef.current) {
      if (lightboxTimeoutRef.current) {
        clearTimeout(lightboxTimeoutRef.current);
        lightboxTimeoutRef.current = null;
      }
      lightboxResolveRef.current();
      lightboxResolveRef.current = null;
    }
  }, [lightbox]);

  const waitForLightbox = useCallback((): Promise<boolean> => {
    if (lightboxRef.current) return Promise.resolve(true);
    return new Promise((resolve) => {
      lightboxResolveRef.current = () => resolve(true);
      lightboxTimeoutRef.current = setTimeout(() => {
        lightboxResolveRef.current = null;
        resolve(false);
      }, LIGHTBOX_TIMEOUT_MS);
    });
  }, []);

  // Mirror auth_origin into a key LB2 does not clear on logout.
  useEffect(() => {
    persistNlbOriginIfPresent();
  }, [lightbox, isAuthenticated]);

  if (context.type !== ResponseType.SUCCESS) {
    return <ErrorPage response={context} />;
  }

  // pubId is already a validated PublicationShortname (Home.server returns SUCCESS only when assertEnum passed).
  const publicationId: PublicationShortname = context.payload.pubId;

  const publicationPath = useMemo<PublicationPathName | null>(
    () =>
      assertEnum(PublicationShortname, context.payload.path) ||
      assertEnum(PublicationSpecialPathName, context.payload.path),
    [context.payload.path],
  );

  // Determine which onboarding to show based on feature flag
  const showNewOnboarding = useMemo(
    () => shouldShowNewOnboarding(publicationId, isNlbUser()),
    [publicationId],
  );

  // Preview mode (EPPR2-3142): a preview-pilot pub whose viewer is a confirmed
  // non-subscriber (and not an NLB patron). Gated on viewerContextResolved so a
  // subscriber never flashes the "Preview Edition" CTA before entitlement is
  // known — until then the default "Start Reading" shows (Design decision Q4).
  const isPreviewMode = useMemo(
    () =>
      isPreviewEnabledForPub(publicationId) &&
      viewerContextResolved &&
      !isNlbUser() &&
      !isEntitledForPub(visitorContext?.svc, publicationId),
    [publicationId, viewerContextResolved, visitorContext?.svc],
  );

  // Preview eligibility is known at render, but entitlement is not until the
  // client viewerContext fetch resolves (~1s post-hydration). During that gap
  // show a loading state, NOT the default Start Reading — otherwise the normal
  // UI flashes before the preview UI (EPPR2-3142, refining Q4).
  //
  // Gate ONLY on the build-baked flag + viewerContextResolved. Both are false
  // at pre-render AND at client-first-render, so SSR === hydration. Do NOT add
  // !isNlbUser() here: isNlbUser() reads sessionStorage (false during SSR, true
  // for an NLB client) so it would diverge from the pre-rendered shell during
  // the hydration window and trip a React hydration mismatch. NLB users and
  // subscribers are excluded from the Preview CTA by isPreviewMode once
  // resolved — they just see the brief spinner then Start Reading, so Q4 holds.
  const isPreviewResolving =
    isPreviewEnabledForPub(publicationId) && !viewerContextResolved;
  const usePreviewLayout = isPreviewMode || isPreviewResolving;

  useEffect(() => {
    if (publicationPath) {
      Cookies.set(COOKIE_PATH_PUBS_ID, publicationPath);
    }
  }, [publicationPath]);

  const [selectedDateMap, setSelectedDateMap] = useState<
    Map<PublicationShortname, Date>
  >(new Map().set(publicationId, new Date()));
  const currentSelectedDate =
    (publicationId && selectedDateMap.get(publicationId)) ?? new Date();

  useEffect(() => {
    i18n.changeLanguage(lang);
  }, [lang, i18n]);

  const gtmDispatch = useEpGTMDispatch();

  useEffect(() => {
    if (publicationId != null) {
      dispatch({
        type: Actions.SET_PUBID,
        payload: { pubId: publicationId },
      });
      Cookies.set(COOKIE_KEY_PUBS_ID, publicationId);
    }
  }, [publicationId]);

  // Derive login state from Lightbox 2.0 context with visitorContext fallback
  const isLoggedIn =
    isAuthenticated ||
    Boolean(visitorContext?.visitorId?.trim());

  // Handle concurrency exceeded — middleware redirects here with #concurrency hash.
  // No auth_status cookie on redirect (avoids LB2 auto-handling).
  // We manually call concurrencyExceeded() with postConcurrencyRedirectUrl for reader redirect.
  useEffect(() => {
    if (!lightbox) return;
    if (typeof window === "undefined") return;
    if (window.location.hash !== "#concurrency") return;

    const lb = lightbox;
    async function handleConcurrency() {
      try {
        const issueId = Cookies.get(COOKIE_ISSUE_ID);
        if (!publicationId || !issueId) return;

        const issue = await getIssueById({
          pubId: publicationId,
          issueId: parseInt(issueId),
        });
        if (issue == null || typeof issue.readerUrlWithPubs !== "string") {
          throw new Error(
            `Reader URL cannot be found :: ${JSON.stringify(issue)}`,
          );
        }

        const readerUrl = new URL(issue.readerUrlWithPubs).toString();

        // Set auth_status cookie for concurrencyExceeded() to read
        document.cookie = `auth_status=CONCURRENCY_EXCEEDED; path=/${publicationId}; max-age=300`;

        lb.concurrencyExceeded({
          postConcurrencyRedirectUrl: readerUrl,
        });

        // Clean up hash from URL
        window.history.replaceState(null, "", window.location.pathname);
      } catch (error) {
        console.error("Failed to handle concurrency exceeded.", error);
      }
    }
    void handleConcurrency();
  }, [lightbox, publicationId]);

  useEffect(() => {
    if (
      visitorContext !== null &&
      !NO_AUTH_PUBLICATION.includes(publicationId)
    ) {
      handleGAEvent(EP_GA_EVENT_NAME.CUSTOM, {
        clickAction: GAClickAction.LOGIN,
        clickLabel: getGAAuthenticationStatus(visitorContext.visitorCategory),
        issue_pub_date: getTrackerDateString(currentSelectedDate),
        chapter1: PAGE_NAME.PORTAL,
        pageName: PAGE_NAME.PORTAL,
      });
    }
  }, [visitorContext?.visitorId, publicationId]);

  useEffect(() => {
    void (async () => {
      try {
        Cookies.set(COOKIE_KEY_PUBS_ID, publicationId);
        setIsLoading(true);
        await fetchApi();
      } catch (e) {
        console.error(e);
      }
    })();
  }, [publicationId]);

  // Portal page-view (IRISX-45). Split out of the fetch effect so preview-
  // eligible pubs can DEFER it until entitlement resolves (~1s) and stamp
  // cd_version correctly; every other pub fires immediately as before. Fires
  // exactly once per pub via the ref guard. Gate on isPreviewMode (resolved +
  // non-entitled), never usePreviewLayout (which includes the resolving gap).
  const portalPvFiredFor = useRef<PublicationShortname | null>(null);
  useEffect(() => {
    if (isPreviewEnabledForPub(publicationId) && !viewerContextResolved) {
      return;
    }
    if (portalPvFiredFor.current === publicationId) {
      return;
    }
    portalPvFiredFor.current = publicationId;
    handleGAEvent(EP_GA_EVENT_NAME.PAGE_VIEW, {
      issue_pub_date: getTrackerDateString(currentSelectedDate),
      chapter1: PAGE_NAME.PORTAL,
      pageName: PAGE_NAME.PORTAL,
      ...(isPreviewMode ? { cd_version: PREVIEW_CD_VERSION } : {}),
    });
  }, [publicationId, viewerContextResolved]);

  useEffect(() => {
    // Don't show onboarding automatically for NLB users - they can manually open via info button
    if (
      getShouldShowOnboarding(publicationId, secondaryPublicationId) &&
      !isNlbUser()
    ) {
      openModal();
    }
  }, [publicationId]);

  useEffect(() => {
    if (issues.length > 0) {
      setIsLoading(false);
    }
  }, [issues]);

  useEffect(() => {
    if (calendarData) {
      const selectedDate = dayjs(currentSelectedDate).format(YMD_FORMAT);
      const selectedIssue = calendarData[selectedDate];

      if (selectedIssue && selectedIssue.length > 0) {
        setIssues(selectedIssue);
      } else {
        fallbackToPreviousDate();
      }
    }

    function fallbackToPreviousDate() {
      if (!calendarData || !publicationId) return;

      const latestAvailableDate = Object.keys(calendarData)
        .reverse() // Reverse the keys array to start from the latest date
        .find(
          (date) =>
            calendarData[date].length > 0 &&
            date < dayjs(currentSelectedDate).format(YMD_FORMAT),
        );

      if (latestAvailableDate) {
        const updatedSelectedDateMap = new Map([
          ...selectedDateMap.entries(),
        ]).set(publicationId, dayjs(latestAvailableDate).toDate());

        setSelectedDateMap(updatedSelectedDateMap);
        setIssues(calendarData[latestAvailableDate]);
      } else {
        setIssues([]);
      }
    }
  }, [calendarData, selectedDateMap]);

  useEffect(() => {
    /**
     * this function is used to check device
     * if the device is mobile or tablet then it will return true
     * it is used to hide the tooltip for mobile and tablet devices
     */
    const handleResize = () => {
      if (typeof window !== "undefined") {
        const userAgent = navigator.userAgent.toLowerCase();
        const isMobile =
          /mobile|android|iphone|ipad|ipod|blackberry|iemobile|opera mini/.test(
            userAgent,
          );
        setIsMobileView(isMobile);
      }
    };

    handleResize();

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const fetchApi = async () => {
    try {
      if (publicationId !== PublicationShortname.DEFAULT) {
        const data = await fetchArchives(publicationId);

        if (data.issues == null) {
          throw new Error("Issues is null");
        }
        // Directly filter issues using the readerUrl provided by the new getArchives payload
        const validatedIssues = data.issues.filter(
          (issue) =>
            issue.id && issue.readerUrl && issue.readerUrl.trim() !== "",
        );

        if (validatedIssues.length === 0) {
          setIsLoading(false);
          return;
        }
        const dataToDisplay: Record<string, TArchive[]> = {};

        for (const issue of validatedIssues) {
          const pointerDate = dayjs(issue.publishedDate).format(YMD_FORMAT);

          if (dataToDisplay[pointerDate]) {
            dataToDisplay[pointerDate].push(issue);
          } else {
            dataToDisplay[pointerDate] = [issue];
          }
        }

        setCalendarData(dataToDisplay);
      }
    } catch (e) {
      console.error(e);
      setIsLoading(false);
    }
  };

  const toggleDrawer = () => {
    setShowDrawer((prevState) => {
      if (!prevState && visitorContext !== null) {
        handleGAEvent(EP_GA_EVENT_NAME.CUSTOM, {
          clickAction: GAClickAction.CLICK,
          clickLabel: ISSUE_GA_CLICK_LABEL.CALENDAR,
          issue_pub_date: getTrackerDateString(currentSelectedDate),
          chapter1: PAGE_NAME.PORTAL,
          pageName: PAGE_NAME.PORTAL,
        });
      }

      return !prevState;
    });
  };

  const openModal = () => {
    setShowModal(true);

    // to call custom GA event on info button click
    if (!getShouldShowOnboarding(publicationId, secondaryPublicationId)) {
      handleGAEvent(EP_GA_EVENT_NAME.CUSTOM, {
        clickAction: GAClickAction.CLICK,
        clickLabel: ISSUE_GA_CLICK_LABEL.INFO_BUTTON,
        issue_pub_date: getTrackerDateString(currentSelectedDate),
        chapter1: PAGE_NAME.PORTAL,
        pageName: PAGE_NAME.PORTAL,
        pagination: 1,
      });
    }

    // to call page view GA when modal is visible
    handleGAEvent(EP_GA_EVENT_NAME.PAGE_VIEW, {
      chapter1: PAGE_NAME.ONBOARDING,
      pageName: PAGE_NAME.ONBOARDING,
      pagination: 1,
    });
  };

  const closeModal = (pagination: number) => {
    setShowModal(false);

    if (getShouldShowOnboarding(publicationId, secondaryPublicationId)) {
      if (secondaryPublicationId) {
        localStorage.setItem(getOnboardingKey(secondaryPublicationId), "true");
      }
      localStorage.setItem(getOnboardingKey(publicationId), "true");
    }

    handleGAEvent(EP_GA_EVENT_NAME.CUSTOM, {
      clickAction: GAClickAction.CLOSE,
      clickLabel: ISSUE_GA_CLICK_LABEL.CLOSE_ONBOARDING_MODAL,
      chapter1: PAGE_NAME.ONBOARDING,
      pageName: PAGE_NAME.ONBOARDING,
      pagination,
    });
  };

  // Core "open the full reader, logging in if needed" flow. onBeforeAuth fires
  // after the issue+cookie are resolved and before the auth branch, matching
  // the exact point the GA click event fired historically — so callers that
  // want the event pass it here and callers that don't (bottom-bar login) omit
  // it. The auth sequence itself is unchanged (see auth-lightbox rules).
  const startReaderLogin = async (
    issue: Partial<TArchive>,
    onBeforeAuth?: () => void,
  ): Promise<void> => {
    if (issue.id == null) {
      throw new Error("Issue ID is null, cannot retrieve reader URL.");
    }

    const resp = await getIssueById({
      pubId: publicationId,
      issueId: issue.id,
    });

    if (resp == null || typeof resp.readerUrlWithPubs !== "string") {
      throw new Error(`Reader URL cannot be found :: ${JSON.stringify(resp)}`);
    }

    Cookies.set(COOKIE_ISSUE_ID, issue.id.toString());
    const readerUrl = new URL(resp.readerUrlWithPubs);

    onBeforeAuth?.();

    if (
      NO_AUTH_PUBLICATION.includes(publicationId) ||
      import.meta.env.IS_BYPASS_AUTH == "true"
    ) {
      window.location.href = readerUrl.toString();
      return;
    }

    // Wait for LB2 script to load — show spinner, timeout after 5s
    if (!lightboxRef.current) {
      setIsWaitingForAuth(true);
      setAuthTimedOut(false);
      const loaded = await waitForLightbox();
      setIsWaitingForAuth(false);
      if (!loaded) {
        setAuthTimedOut(true);
        return;
      }
    }

    // NLB patrons authenticate on NLB eResources, not mySPH, so an expired
    // session must route to the dead-end page — never a mySPH login they
    // cannot complete (EPPR2-3315).
    if (await isNlbSessionExpired(publicationId)) {
      window.location.href = NLB_SESSION_EXPIRED_URL;
      return;
    }

    // Always go through LB2 login — sets fresh cookies on the correct
    // publication path via saveCookiePath(). LB2 SSO handles silently
    // if already authenticated. Cannot skip this because cookies are
    // path-scoped and isAuthenticated is global (Ory session).
    //
    // Pass the base pubId (not the URL path) so the single-flavor pages
    // (/zb-single etc.) scope cookies to /{pub}, where the reader and BFF
    // live — otherwise the reader gets no cookie and bounces (IRISX-42).
    saveCookiePath(publicationId ?? undefined);
    lightboxRef.current?.initiateLogin({
      postLoginRedirectUrl: readerUrl.toString(),
    });
  };

  const handleIssueRedirect =
    (source: ISSUE_GA_CLICK_LABEL, issue: Partial<TArchive>) => async () => {
      try {
        await startReaderLogin(issue, () => {
          handleGAEvent(EP_GA_EVENT_NAME.CUSTOM, {
            clickAction: GAClickAction.CLICK,
            clickLabel: source,
            issue_pub_date: getTrackerDateString(currentSelectedDate),
            chapter1: PAGE_NAME.PORTAL,
            pageName: PAGE_NAME.PORTAL,
          });
        });
      } catch (error) {
        // pop up if needed
        console.error(error);
      }
    };

  // Preview CTA: route the non-subscriber into the un-gated preview reader for
  // the displayed day's issue. If no preview issue exists (e.g. CUE stopped
  // generating), fall back to today's login flow so the CTA is never dead.
  const handlePreviewRedirect = async (): Promise<void> => {
    try {
      if (!publicationId) return;
      handleGAEvent(EP_GA_EVENT_NAME.CUSTOM, {
        clickAction: GAClickAction.CLICK,
        clickLabel: ISSUE_GA_CLICK_LABEL.PREVIEW_ISSUE,
        issue_pub_date: getTrackerDateString(currentSelectedDate),
        chapter1: PAGE_NAME.PORTAL,
        pageName: PAGE_NAME.PORTAL,
        pagination: 1,
        cd_version: PREVIEW_CD_VERSION,
      });
      const { issues: previewIssues } = await fetchPreviewArchive(
        `${publicationId}preview`,
      );
      const previewIssue = pickPreviewIssueForDate(
        previewIssues,
        currentSelectedDate,
      );
      if (!previewIssue) {
        await startReaderLogin(issues[0]);
        return;
      }
      window.location.href = previewIssue.readerUrl;
    } catch (error) {
      console.error(error);
    }
  };

  // Bottom-bar "Log in" (anonymous users): the same full-reader login as Start
  // Reading, minus the GA event (preview analytics are EPPR2-3146). A
  // non-entitled login then lands on the middleware's subscribe redirect.
  const handleSubscriberLogin = async (): Promise<void> => {
    try {
      if (issues.length === 0) return;
      await startReaderLogin(issues[0]);
    } catch (error) {
      console.error(error);
    }
  };

  // Preview subscribe CTAs (bottom bar + archive/calendar gate modal) go
  // straight to the publication subscribe page (Design decision Q2).
  const handlePreviewSubscribe = (): void => {
    handleGAEvent(EP_GA_EVENT_NAME.CUSTOM, {
      clickAction: GAClickAction.CLICK,
      clickLabel: ISSUE_GA_CLICK_LABEL.EXPLORE_SUBSCRIPTION,
      issue_pub_date: getTrackerDateString(currentSelectedDate),
      chapter1: PAGE_NAME.PORTAL,
      pageName: PAGE_NAME.PORTAL,
      pagination: 1,
      cd_version: PREVIEW_CD_VERSION,
    });
    const url = publicationId
      ? PREVIEW_SUBSCRIBE_URL[publicationId]
      : undefined;
    if (url) {
      window.location.href = url;
    }
  };

  const openPreviewGate = () => {
    setShowPreviewGate(true);
  };
  const closePreviewGate = () => {
    setShowPreviewGate(false);
  };

  const handleGAEvent = (eventName: EP_GA_EVENT_NAME, gtmData?: TGtmData) => {
    if (visitorContext !== null) {
      const GAContext = NO_AUTH_PUBLICATION.includes(publicationId)
        ? DEFAULT_VISITOR_CONTEXT
        : visitorContext;

      gtmDispatch({
        eventName: eventName,
        eventDetail: {
          ...gtmData,
          visitorCategory: GAContext.visitorCategory,
          visitorId: GAContext.visitorId,
          svc: GAContext.svc,
          orientation: getScreenOrientation(),
          publication_name: getGAPublicationName(publicationId),
        },
      });
    }
  };

  const handleLogout = () => {
    handleGAEvent(EP_GA_EVENT_NAME.CUSTOM, {
      clickAction: GAClickAction.CLICK,
      clickLabel: ISSUE_GA_CLICK_LABEL.LOGOUT_BUTTON,
      issue_pub_date: getTrackerDateString(currentSelectedDate),
      chapter1: PAGE_NAME.PORTAL,
      pageName: PAGE_NAME.PORTAL,
    });

    //clear visitor context from context on log out
    dispatch({
      type: Actions.SET_VIEWERCONTEXT,
      payload: { visitorContext: DEFAULT_VISITOR_CONTEXT },
    });
  };

  const toggleTileView = () => {
    if (!showTileView) {
      handleGAEvent(EP_GA_EVENT_NAME.CUSTOM, {
        clickAction: GAClickAction.CLICK,
        clickLabel: ISSUE_GA_CLICK_LABEL.TILE_VIEW_BUTTON,
        issue_pub_date: getTrackerDateString(currentSelectedDate),
        chapter1: PAGE_NAME.PORTAL,
        pageName: PAGE_NAME.PORTAL,
        pagination: 1,
      });
    }
    setTileView((preState) => !preState);
  };

  return (
    <div
      className={cx(styles.homeWrapper, {
        [styles.previewMode]: usePreviewLayout,
      })}
    >
      {h1Text && <h1 className="sr-only">{h1Text}</h1>}
      <HomePageContext.Provider
        value={{
          toggleDrawer,
          setSelectedDateMap,
          calendarData,
          isMobileView,
          selectedDateMap,
          issues,
        }}
      >
        <Header
          publicationId={publicationId}
          handleLogout={handleLogout}
          isLoggedIn={isLoggedIn}
          openModal={openModal}
          toggleDrawer={usePreviewLayout ? openPreviewGate : toggleDrawer}
          toggleTileView={usePreviewLayout ? openPreviewGate : toggleTileView}
          showTileView={showTileView}
        />
        {secondaryPublicationId && publicationId && (
          <MultiTitle
            secondaryPublicationId={secondaryPublicationId}
            publicationId={publicationId}
            navTabBarIndex={navTabBarIndex}
          />
        )}
        <div
          className={cx({
            [styles.homeContainer]: true,
            [styles.hideHomeContainer]: showDrawer && isMobileView,
            [styles.multiTitleIssueContainer]:
              secondaryPublicationId && publicationId,
          })}
        >
          <div
            className={cx([
              styles.readerViewContainer,
              showTileView && styles.hideReaderView,
            ])}
          >
            <div>
              <div
                className={cx(styles.dateContainer)}
                data-testid="dateContainer"
              >
                <div className={cx(styles.dayTxt)} data-theme={publicationId}>
                  {useDateFormat({
                    date: currentSelectedDate,
                    shouldShowDayOfWeek: true,
                  })}
                </div>
                <div className={cx(styles.dateTxt)} data-theme={publicationId}>
                  {useDateFormat({ date: currentSelectedDate })}
                </div>
              </div>
              {isLoading ? (
                <div className={cx(styles.pubsRow)}>
                  <IssueContainer
                    isMultiTitle={Boolean(secondaryPublicationId && publicationId)}
                    theme={publicationId}
                    showTileView={showTileView}
                    showButton={!usePreviewLayout}
                  />
                </div>
              ) : (
                <div className={cx(styles.pubsRow)}>
                  {issues.length > 0 && (
                    <IssueContainer
                      theme={publicationId}
                      showTileView={showTileView}
                      isMultiTitle={Boolean(secondaryPublicationId && publicationId)}
                      imageSrc={issues[0].coverImageUri}
                      isWaitingForAuth={isWaitingForAuth}
                      authTimedOut={authTimedOut}
                      isPreview={usePreviewLayout}
                      showButton={!usePreviewLayout}
                      handleButtonClick={
                        isPreviewMode
                          ? handlePreviewRedirect
                          : handleIssueRedirect(
                              ISSUE_GA_CLICK_LABEL.START_READING_BTN,
                              issues[0],
                            )
                      }
                      handleImageClick={
                        isPreviewMode
                          ? handlePreviewRedirect
                          : handleIssueRedirect(
                              ISSUE_GA_CLICK_LABEL.IMAGE_CLICK,
                              issues[0],
                            )
                      }
                    />
                  )}
                </div>
              )}
            </div>
          </div>
          {showTileView && (
            <div className={cx(styles.tileViewWrapper)}>
              <TileView
                issues={calendarData}
                theme={publicationId}
                toggleTileView={toggleTileView}
              />
            </div>
          )}
        </div>
        {showModal &&
          (showNewOnboarding ? (
            <OnboardingOverlay
              onClose={() => {
                closeModal(1);
              }}
            />
          ) : (
            <OnboardingModal
              onClose={closeModal}
              handleGAEvent={handleGAEvent}
              publicationId={publicationId}
            />
          ))}
        {showDrawer && (
          <DrawerContainer
            showDrawer={showDrawer}
            theme={publicationId}
            toggleDrawer={toggleDrawer}
            isMobileView={isMobileView}
          />
        )}
        {isPreviewResolving && !isLoading && (
          <div className={cx(styles.previewCta)}>
            <Lottie
              options={{
                loop: true,
                autoplay: true,
                animationData: ButtonLoading,
              }}
              height={40}
              width={40}
            />
          </div>
        )}
        {isPreviewMode && issues.length > 0 && (
          <div className={cx(styles.previewCta)}>
            <button
              className={cx(styles.previewCtaBtn)}
              data-theme={publicationId}
              onClick={() => void handlePreviewRedirect()}
            >
              {t("home:actions.PreviewEdition")}
            </button>
          </div>
        )}
        {isPreviewMode && (
          <PreviewBottomBar
            theme={publicationId}
            onSubscribe={handlePreviewSubscribe}
            onLogin={handleSubscriberLogin}
            showLogin={!isLoggedIn}
          />
        )}
        {showPreviewGate && (
          <PreviewGateModal
            theme={publicationId}
            onClose={closePreviewGate}
            onSubscribe={handlePreviewSubscribe}
          />
        )}
      </HomePageContext.Provider>
    </div>
  );
}

export default HomePage;
